import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSwipeable } from "react-swipeable";
import classNames from "classnames";
import MutationObserver from "mutation-observer";

import Button from "../../Buttons/Button";
import { MapPanelContext } from "./MapPanel";
import withMapPanelSizing from "./withMapPanelSizing";
import { useMapContext } from "../MapContext";
import { useMoveMapCenterTo } from "../moveMapCenterTo";
import { useDeferredEffect } from "../../Utils/useDeferredEffect";

import { openMapPanel, closeMapPanel } from "../../../reducers/map/panels";
import { defineMessages } from "react-intl";
import { useFormatMessage } from "../../Intl/Context";
import FeatureTooltip from "../../Tooltip/FeatureTooltip";
import { isTouchDevice } from "../../Utils/isTouchDevice";
import { useStickyElementsHeight } from "../../Utils/StickyElementsHeight";

export const MAP_PANEL_MOBILE_CLOSED_OFFSET = 100;
export const MAP_PANEL_MOBILE_OPEN_OFFSET = 100;

const MAP_PANEL_HEADER_PADDING_OFFSET = 30;

const MapPanelMobile = ({
  style = {},
  isToggleable = true,
  keepMapVisibleOnOpen = false,
  ...props
}) => {
  const { id, height: defaultHeight, containerPanelRef, innerPanelRef, children } = props;
  const { mapKey } = useMapContext();

  const activePanelSelector = map => map[mapKey].panels.activePanels[id];
  const isShown = useSelector(({ map }) => activePanelSelector(map).isShown);
  const isOpen = useSelector(({ map }) => activePanelSelector(map).isOpen);
  const mapElement = useSelector(({ map }) => activePanelSelector(map).mapElement);
  const stickyElementsHeight = useStickyElementsHeight();

  const { latitude, longitude } = mapElement || {};

  const dismissTooltipRef = useRef();
  const dispatch = useDispatch();

  const setMapPanelOpen = useCallback(() => {
    dispatch(openMapPanel(mapKey, id));

    if (dismissTooltipRef.current) dismissTooltipRef.current();
  }, [dispatch, id, mapKey]);

  const setMapPanelClosed = useCallback(
    () => dispatch(closeMapPanel(mapKey, id)),
    [dispatch, id, mapKey]
  );

  const [header, setHeader] = useState();
  const [panelSwipeOffsetY, setPanelSwipeOffsetY] = useState(0);
  const [previousSwipeAbsDeltaY, setPreviousSwipeAbsDeltaY] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [containerPanel, setContainerPanel] = useState();
  const [innerPanel, setInnerPanel] = useState();
  const [preventInnerPanelScrolling, setPreventInnerPanelScrolling] = useState(true);
  const [mapPanelActionsContainer, setMapPanelActionsContainer] = useState();
  const containerPanelObserverRef = useRef();

  const moveMapCenterTo = useMoveMapCenterTo();

  const initialInnerPanelScrollTopRef = useRef(null);

  const cacheHeaderContainer = useCallback(() => {
    if (!containerPanel) return;

    setHeader(containerPanel.querySelector(".map-panel-header"));
  }, [containerPanel]);

  // When the container panel is rendered, this callback ref method is called, which
  // bubbles up the containerPanelRef from the props and sets the containerPanel into
  // the state, and also refreshes the state header.
  const setContainerPanelRef = useCallback(
    containerPanel => {
      containerPanelRef(containerPanel);
      setContainerPanel(containerPanel);
      cacheHeaderContainer();
    },
    [containerPanelRef, cacheHeaderContainer]
  );

  // When the containerPanel is set, add a mutation observer to its content so whenever
  // its content change, we check for a potential header container inside of its content
  // and possibly readapt the panel behavior to take it into account
  useEffect(() => {
    if (!containerPanel) return;

    containerPanelObserverRef.current = new MutationObserver(cacheHeaderContainer);
    containerPanelObserverRef.current.observe(containerPanel, {
      childList: true,
      subtree: true
    });

    return () => containerPanelObserverRef.current?.disconnect();
  }, [containerPanel, cacheHeaderContainer]);

  const setInnerPanelRef = useCallback(
    innerPanel => {
      innerPanelRef(innerPanel);
      setInnerPanel(innerPanel);
    },
    [innerPanelRef]
  );

  const headerHeight = header?.clientHeight
    ? header.clientHeight + MAP_PANEL_HEADER_PADDING_OFFSET
    : 0;

  const defaultClosedOffset = props.closedOffset || 0;
  const offset =
    header && header.clientHeight
      ? headerHeight + defaultClosedOffset
      : MAP_PANEL_MOBILE_CLOSED_OFFSET;

  const closedOffset = containerPanel
    ? Math.min(offset, containerPanel.offsetHeight)
    : offset;

  const actionsContainer = containerPanel?.querySelector(".map-panel-actions");
  const actionsHeight = actionsContainer?.offsetHeight || 0;

  const availableHeight = window.innerHeight - actionsHeight - stickyElementsHeight;
  const maxHeight = keepMapVisibleOnOpen
    ? availableHeight - MAP_PANEL_MOBILE_OPEN_OFFSET
    : availableHeight;

  // if the defaultHeight provided by the withMapPanelSizing HOC is set as "auto",
  // retrieve the height from the panel DOM node's clientHeight, otherwise, use the
  // provided height as it's processed from the available height
  const panelHeight =
    defaultHeight === "auto" ? containerPanel?.clientHeight : defaultHeight;
  const openPanelHeight = containerPanel ? Math.min(panelHeight, maxHeight) : 0;
  const height =
    defaultHeight === "auto" && panelHeight < maxHeight ? "auto" : openPanelHeight;

  const baseBottomOffset = actionsHeight;

  const bottom = useMemo(() => {
    if (!isShown) return null;

    if (isOpen) {
      return baseBottomOffset;
    } else if (height && height !== "auto") {
      return baseBottomOffset + closedOffset - height;
    } else if (containerPanel && height === "auto") {
      return baseBottomOffset + closedOffset - containerPanel.clientHeight;
    } else {
      return null;
    }
  }, [isShown, isOpen, baseBottomOffset, height, closedOffset, containerPanel]);

  const handleSwiping = useCallback(
    ({ dir, deltaY }) => {
      if (!containerPanel) return;

      // Get the inner panel current scroll position, to check whether the contents of the
      // panel were scrolled down before a swipe up action
      const scrollTop = (innerPanel && innerPanel.scrollTop) || 0;

      // Store the initial scroll value of the inner panel when the swipe starts, so we
      // can use it as an offset to substract from the swipe delta if the user has to scroll
      // the inner panel before we move the panel down while he swipes
      if (initialInnerPanelScrollTopRef.current === null) {
        initialInnerPanelScrollTopRef.current = scrollTop;
      }

      // If we're swiping down, but the innerPanel is not scrolled to the top yet, just let
      // the user scroll the panel and avoid moving the panel until it reaches the top
      // of the panel scroll position
      if (dir === "Down" && scrollTop > 0) return;

      // Whether the user reached the top of the inner panel scroll position, or if the
      // panel was not scrolled, as we'll be moving the panel following the swipe action,
      // ensure that the panel is not scrolled
      if (dir === "Down" && scrollTop === 0 && !preventInnerPanelScrolling) {
        setPreventInnerPanelScrolling(true);
      }

      const previousSwipeAbsDeltaY = Math.abs(panelSwipeOffsetY);
      let newPanelSwipeOffsetY = 0;

      // If the panel was open, only allow swiping under the Y position it had when it was
      // open.
      if (isOpen && deltaY > 0) {
        const deltaWithoutScrolled = deltaY - initialInnerPanelScrollTopRef.current;
        // Ensure that the panel is not swiped lower than the target Y position it will
        // have once it's closed
        const minOffsetY = -openPanelHeight + closedOffset;
        newPanelSwipeOffsetY =
          -deltaWithoutScrolled < minOffsetY ? minOffsetY : -deltaWithoutScrolled;
        // If the panel was closed, only allow swiping upper than the Y position it had when
        // it was closed
      } else if (!isOpen && deltaY < 0) {
        const maxOffsetY = openPanelHeight - closedOffset;
        newPanelSwipeOffsetY = -deltaY > maxOffsetY ? maxOffsetY : -deltaY;

        // Ensure that the feature tooltip is dismissed when the panel is swiped up
        if (dismissTooltipRef.current) dismissTooltipRef.current(true);
      }

      setPanelSwipeOffsetY(newPanelSwipeOffsetY);
      setPreviousSwipeAbsDeltaY(previousSwipeAbsDeltaY);
      setIsSwiping(true);
    },
    [
      closedOffset,
      containerPanel,
      innerPanel,
      isOpen,
      openPanelHeight,
      panelSwipeOffsetY,
      preventInnerPanelScrolling
    ]
  );

  const resetSwipingState = useCallback(() => {
    setIsSwiping(false);
    setPanelSwipeOffsetY(0);
    setPreviousSwipeAbsDeltaY(0);
    initialInnerPanelScrollTopRef.current = null;
  }, [setIsSwiping, setPanelSwipeOffsetY, setPreviousSwipeAbsDeltaY]);

  const handleSwipedUp = useCallback(
    ({ absY }) => {
      resetSwipingState();

      // Define whether the panel will be open after the swipe up depending on the user
      // having swiped far enough
      const willOpen = !isOpen && absY > 50 && previousSwipeAbsDeltaY <= absY;
      if (willOpen) setMapPanelOpen();

      // If the panel will not open and stays closed, ensure that the user cannot scroll
      // the contents of the inner panel
      const shouldPrevenPanelScrolling = !willOpen && !isOpen;
      setPreventInnerPanelScrolling(shouldPrevenPanelScrolling);
    },
    [isOpen, previousSwipeAbsDeltaY, resetSwipingState, setMapPanelOpen]
  );

  const handleSwipedDown = useCallback(
    ({ absY }) => {
      resetSwipingState();

      // If the inner panel was not scrolled to the top, abort swiping
      const scrollTop = (innerPanel && innerPanel.scrollTop) || 0;
      if (scrollTop > 0) return;

      // Define whether the panel will be closed after the swipe down depending on the user
      // having swiped far enough
      const willClose = isOpen && absY > 50 && previousSwipeAbsDeltaY <= absY;
      if (willClose) setMapPanelClosed();

      const shouldPrevenPanelScrolling = willClose || !isOpen;
      // If the panel will not close and stays open, ensure that we let the user scroll
      // the contents of the inner panel
      setPreventInnerPanelScrolling(shouldPrevenPanelScrolling);
    },
    [innerPanel, isOpen, previousSwipeAbsDeltaY, resetSwipingState, setMapPanelClosed]
  );

  const handlePanelClicked = useCallback(
    () => !isOpen && setMapPanelOpen(),
    [isOpen, setMapPanelOpen]
  );
  const handleToggleClicked = useCallback(
    () => (isOpen ? setMapPanelClosed() : setMapPanelOpen()),
    [isOpen, setMapPanelClosed, setMapPanelOpen]
  );

  // Reset panel scrollTop and prevent scrolling when the panel is closed
  useEffect(() => {
    if (!containerPanel || isOpen) return;

    containerPanel.querySelector(".map-panel-inner").scrollTop = 0;
    setPreventInnerPanelScrolling(true);
  }, [containerPanel, isOpen]);

  // Restore map center to target element when panel is open or closed
  useDeferredEffect(
    () => {
      if (!latitude || !longitude) return;

      moveMapCenterTo(latitude, longitude);
    },
    [isOpen, moveMapCenterTo, latitude, longitude],
    { wait: 200 }
  );

  const swipeableProps = useSwipeable({
    onSwiping: handleSwiping,
    onSwipedUp: handleSwipedUp,
    onSwipedDown: handleSwipedDown
  });

  const t = useFormatMessage();
  const tooltipDescription = t(intlMessages.tooltipDescription);

  const className = classNames([
    "map-panel",
    "map-panel-mobile",
    "box-shadow-lg",
    props.className,
    {
      "map-panel-mobile-shown": isShown,
      "map-panel-mobile-open": isOpen,
      "map-panel-mobile-closed": !isOpen,
      "map-panel-mobile-swiping": isSwiping,
      "map-panel-mobile-toggleable": isToggleable
    }
  ]);

  // Ensure that current device has touch support before preventing the scroll, to allow
  // desktop 200% zooming to scroll it, for accessibility purposes
  const innerPanelClassName = classNames("map-panel-inner", {
    "overflow-hidden": preventInnerPanelScrolling && isTouchDevice
  });

  // Disable default bottom style property transition while swiping so the panel follows
  // the actual swipe and is not delayed
  const transition = isSwiping ? "bottom 0s" : null;

  const display = isShown ? "block" : "none";
  const bottomStyle = bottom !== null ? bottom + panelSwipeOffsetY : null;
  style = { bottom: bottomStyle, height, display, transition, ...style };

  return (
    <MapPanelContext.Provider value={{ mapPanelActionsContainer }}>
      <div
        className={className}
        style={style}
        ref={setContainerPanelRef}
        onClick={handlePanelClicked}
        data-map-panel={id}
      >
        <div className="h-100" {...swipeableProps}>
          {isToggleable && (
            <Button
              type="button"
              className="map-panel-mobile-toggle-button"
              onClick={handleToggleClicked}
            >
              <div className="map-panel-mobile-toggle-button-inner" />
            </Button>
          )}

          <FeatureTooltip
            featureName="mapPanelMobileTooltip"
            message={tooltipDescription}
            dismissTooltipRef={dismissTooltipRef}
            appearFrom="top"
            placement="top"
            show={isToggleable}
          >
            <div className={innerPanelClassName} ref={setInnerPanelRef}>
              <div className="padding-xs boxes-list">{children}</div>
            </div>
          </FeatureTooltip>

          <div
            className="map-panel-actions-container"
            ref={setMapPanelActionsContainer}
          />
        </div>
      </div>
    </MapPanelContext.Provider>
  );
};

export default withMapPanelSizing(MapPanelMobile);

const intlMessages = defineMessages({
  tooltipDescription: {
    id: "map.mobile.panel.tooltipDescription",
    defaultMessage: `
      Cliquez ou faites glisser le panneau afin de découvrir son contenu.
    `
  }
});
